import React, { useEffect, useState } from 'react';
import { graphql, Link } from 'gatsby';
import { css, Global } from '@emotion/react';
import { formatDates, formatDateTimes } from '../utility';
import { GatsbyImage } from 'gatsby-plugin-image';
import Vimeo from '@u-wave/react-vimeo';
import SEO from '../components/seo';
import ConditionalWrapper from '../components/conditional-wrapper';
import PortableText from '../components/portable-text';
import Accordion from '../components/accordion';
import Clamp from '../components/clamp';
import Gallery from '../components/gallery';
import ProgramCard from '../components/program-card';
import Play from '../svg/play.svg';

export default function Program({data, setMenuAbsolute, setActivePage, setShowExtra, setShowInfo, setImagesModal, showImages, setShowImages, setEmbed}) {
  const [hideImage, setHideImage] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [player, setPlayer] = useState(null);

  const people = data.program.people.filter(person => person).map(person => {
    const regex = new RegExp(`^${person.name}`);

    if (person._rawBiography && person._rawBiography[0]?._type === 'block') {
      person._rawBiography[0].children[0].text = person._rawBiography[0].children[0].text.replace(regex, ``);

      person._rawBiography[0].children.unshift({
        marks: ['strong'],
        text: person.name,
        _type: 'span',
      });
    }

    return person;
  });

  const curators = data.program.curators.filter(person => person).map(person => {
    const regex = new RegExp(`^${person.name}`);

    if (person._rawBiography && person._rawBiography[0]?._type === 'block') {
      person._rawBiography[0].children[0].text = person._rawBiography[0].children[0].text.replace(regex, ``);

      person._rawBiography[0].children.unshift({
        marks: ['strong'],
        text: person.name,
        _type: 'span',
      });
    }

    return person;
  });

  let tags = (data.program.location) ? [data.program.location.charAt(0).toUpperCase() + data.program.location.slice(1)] : [];
  tags = tags.concat(data.program.type.map((el) => el.title));
  tags = tags.concat(data.program.series.map((el) => el.title));

  useEffect(() => {
    setMenuAbsolute(false);
    setActivePage('program');
    setShowExtra(true);
    setShowInfo(false);
    setImagesModal(true);
  }, []);

  return (
    <div
      css={css`
        margin-top: 6rem;
      `}
    >
      <SEO
        title={data.program.title}
        description={(data.program._rawOverview) ? data.program._rawOverview.map(el => el.children).flat().map((el) => el?.text).join('\n\n') : null}
        image={(data.program.featureImage) ? data.program.featureImage.asset?.gatsbyImageData.images.fallback?.srcSet.split(' ').reverse()[1].split('\n')[1] : null}
      />
      {data.program.location === 'online' && <Global
        styles={css`
          :root {
            --black: var(--mutedPink);
            --white: var(--darkBrown);
            --20Brown: rgba(255, 255, 255, 0.2);
            --mutedBrown: var(--mutedPink);
            --oldBrown: var(--mutedPink);
            --purple: var(--mutedPink);
            --outline: var(--black);
            --online: block;
            --offline: none;
          }
        `}
      />}
      <figure
        css={css`
          margin-bottom: 4.5rem;
        `}
      >
        {(data.program.featureImage && !data.program.featureVideo) &&
          <div
            css={css`
              position: relative;
              z-index: 1;

              &:hover {
                .program-eye {
                  display: block;
                }
              }
            `}
          >
            <GatsbyImage
              image={data.program.featureImage.asset?.gatsbyImageData}
              loading="eager"
              alt={data.program.featureImage.asset?.altText}
              css={css`
                cursor: pointer;
              `}
              onClick={() => {
                setShowImages(true);
                setCurrentImage(0); 
              }}
            />
            <img
              className="program-eye"
              css={css`
                display: none;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                pointer-events: none;
              `}
              src="/images/eye.svg"
            />
          </div>
        }
        {data.program.featureVideo &&
          <div
            css={css`
              position: relative;
              z-index: 1;

              &:hover {
                svg path {
                  fill: var(--black);
                }
              }
            `}
          >
            <div
              css={css`
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background-image: url(${data.program.featureImage?.asset.gatsbyImageData.images.fallback.srcSet.split(' ').reverse()[1].split('\n')[1]});
                background-size: cover;
                opacity: ${(hideImage) ? 0 : 1};
                z-index: 1;
                cursor: pointer;
              `}
              onClick={() => {
                player?.getPaused().then((result) => {
                  if (result) {
                    player?.play();
                  } else {
                    player?.pause();
                  }
                });

                setHideImage(!hideImage);
              }}
            ></div>
            <Vimeo
              video={data.program.featureVideo}
              controls={false}
              onReady={(player) => setPlayer(player)}
              onPause={(player) => setHideImage(false)}
              color="D8B0B0"
              showPortrait={false}
              showTitle={false}
              showByline={false}
              responsive
            />
            <div
              css={css`
                display: ${(hideImage) ? 'none' : 'block'};
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                pointer-events: none;
                z-index: 2;

                @media (max-width: 700px) {
                  svg {
                    width: 50px;
                  }
                }

                svg path {
                  fill: var(--white);
                }
              `}
            >
              <Play />
            </div>
          </div>
        }
        <figcaption className="type--smaller">{data.program.featureImage && data.program.featureImage.asset?.description}</figcaption>
      </figure>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          margin-bottom: 2rem;

          p:last-of-type {
            margin-bottom: 3.6rem;
          }
        `}
      >
        <div
          css={css`
            grid-column: span 8;

            @media (max-width: 700px) {
              grid-column: span 12;
            }

            h1, h2 {
              margin: 0 0 3rem 0;
            }

            h2:last-of-type {
              margin-bottom: 6rem;
            }
          `}
        >
          <h1 className="type--large">{data.program.title}</h1>
          <h2
            className="type--large"
            css={css`
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 5;
              padding-bottom: 1.5rem;
              overflow: hidden;

              @media (max-width: 700px) {
                padding-bottom: 0;
              }
            `}
          >
            {people.length > 0 && people
              .map(person => <span key={person.id}>{person.name}</span>)
              .reduce((prev, curr) => [prev, ', ', curr])
            }
          </h2>
          <h2 className="type--large">{formatDates(data.program.startDate, data.program.endDate)}</h2>
          {data.program._rawContent &&
            <PortableText
              css={css`
                h3 {
                  font-family: 'ROM Condensed', sans-serif;
                  font-weight: bold;
                  font-size: 6rem;
                  line-height: 1;
                  letter-spacing: -0.015em;
                  margin-bottom: 3rem;

                  @media (max-width: 700px) {
                    font-size: 4.5rem;
                  }
                }

                img {
                  width: 100%;
                }

                .indent {
                  padding-left: 20rem;
                }

                @media (max-width: 700px) {
                  margin-top: 7rem;
                }
              `}
              blocks={data.program._rawContent}
              setEmbed={setEmbed}
            />
          }
          {data.program.images.length > 0 &&  <div
            css={css`
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: var(--smallGutter);
              margin-top: 10rem;

              @media (max-width: 700px) {
                figure {
                  grid-column: span 3;
                }
              }

              img {
                cursor: pointer;

                &:hover {
                  filter: grayscale(100%);
                }
              }
            `}
          >
            {data.program.images
              .map((image, i) => (
                <figure
                  key={i}
                  css={css`
                    @media (max-width: 700px) {
                      margin-bottom: 3rem;
                    }

                    .program-gallery--desktop {
                      @media (max-width: 700px) {
                        display: none;
                      }
                    }

                    .program-gallery--mobile {
                      display: none;

                      @media (max-width: 700px) {
                        display: block;
                      }
                    }
                  `}
                >
                  <GatsbyImage
                    image={image.asset?.gatsbyImageData}
                    alt={image.asset?.altText}
                    className="program-gallery--desktop"
                    style={
                      {
                        maxHeight: 'calc(20vw - (var(--smallGutter) * 2))',
                        marginTop: ((image.asset?.gatsbyImageData.width / image.asset?.gatsbyImageData.height) > 1) ? '12%' : null,
                      }
                    }
                    objectFit="contain"
                    loading="eager"
                    onClick={() => {
                      setShowImages(true);
                      setCurrentImage(i + 1); 
                    }}
                  />
                  <GatsbyImage
                    image={image.asset?.gatsbyImageData}
                    alt={image.asset?.altText}
                    className="program-gallery--mobile"
                    style={
                      {
                        width: '100%',
                      }
                    }
                    objectFit="contain"
                    loading="eager"
                    onClick={() => {
                      setShowImages(true);
                      setCurrentImage(i + 1); 
                    }}
                  />
                  <figcaption
                    className="type--smaller"
                    css={css`
                      color: var(--black);
                      margin-top: 1.5rem;
                    `}
                  >
                    <Clamp lines={3}>{image.asset?.description}</Clamp>
                  </figcaption>
                </figure>
              ))
            }
          </div>}
          {data.program.publications.length > 0 && <div
            css={css`
              display: grid;
              grid-template-columns: repeat(8, 1fr);
              gap: var(--gutter);
              margin-top: 10rem;
            `}
          >
            {data.program.publications.map((item, i) => (
              <Link
                key={i}
                to={`/publication/${item.slug?.current}`}
                css={css`
                  grid-column: span 4;
                  text-decoration: none;

                  &:hover {
                    > div:first-of-type {
                      color: var(--white);
                      background-color: var(--purple);
                    }
                  }
                `}
              >
                <div
                  css={css`
                    position: relative;
                    color: var(--purple);
                    padding-bottom: 100%;
                    border: 1px solid var(--purple);
                    border-radius: 100%;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      position: absolute;
                      top: 0;
                      right: 0;
                      bottom: 0;
                      left: 0;
                      text-align: center;
                      padding: var(--smallGutter);
                    `}
                  >
                    {item.title}<br />
                    {item.author}
                  </div>
                </div>
              </Link>
            ))}
          </div>}
          <div
            className="type--mono"
            css={css`
              margin-top: 15.5rem;

              @media (max-width: 700px) {
                display: none;
              }
            `}
          >
            {tags.length > 0 && tags
              .map((tag, i) => <span key={i}>{tag}</span>)
              .reduce((prev, curr) => [prev, ', ', curr])
            }
          </div>
        </div>
        <div
          css={css`
            grid-column: span 4;
            margin-top: 1.5rem;

            @media (max-width: 700px) {
              grid-column: span 12;
              margin-bottom: 5rem;
            }

            p {
              margin-bottom: 1em;

              &:last-of-type {
                margin-bottom: 0;
              }
            }
          `}
        >
          <div
            css={css`
              position: sticky;
              top: var(--smallGutter);
            `}
          >
            <div
              css={css`
                border-top: 1px solid var(--black);
              `}
            >
              {data.program._rawOverview &&
                <Accordion title="Overview" open={true}>
                  <PortableText
                    blocks={data.program._rawOverview}
                    setEmbed={setEmbed}
                  />
                </Accordion>
              }
              {data.program._rawKeyDatesTimes?.length > 0 &&
                <Accordion title="Key Dates" open={false}>
                  {data.program._rawKeyDatesTimes.length > 0 && data.program._rawKeyDatesTimes.map(date =>
                    <div key={date._key}>
                      <ConditionalWrapper
                        condition={date.link}
                        wrapper={children => <a target="_blank" rel="noreferrer" href={date.link}>{children}</a>}
                      >
                        {date.title}</ConditionalWrapper>: {(date._type === 'dateRange') ?
                          formatDates(date.startDate, date.endDate) :
                          formatDateTimes(date.startDateTime, date.endDateTime)}
                    </div>
                  )}
                </Accordion>
              }
              {data.program.acknowledgement &&
                <Accordion title="Acknowledgment" open={false}>
                  <PortableText
                    blocks={data.program.acknowledgement && data.program.acknowledgement._rawContent}
                    setEmbed={setEmbed}
                  />
                </Accordion>
              }
              {people.length > 0 &&
                <Accordion title="Biographies" open={false}>
                  {people
                    .map((person, i) => {
                      if (person._rawBiography) {
                        return (
                          <Clamp 
                            key={person.id}
                            css={css`
                              margin-bottom: 2rem;
                            `}
                            lines="5"
                            forceClamp
                          >
                            <PortableText
                              blocks={person._rawBiography}
                              setEmbed={setEmbed}
                            />
                          </Clamp>
                        )
                      } else {
                        return (
                          <div 
                            key={person.id}
                            css={css`
                              font-weight: bold;
                              margin-bottom: 2rem;
                            `}
                          >
                            {person.name}
                          </div>
                        )
                      }
                    })
                  }
                </Accordion>
              }
              {curators.length > 0 &&
                <Accordion title="Curators" open={false}>
                  {curators
                    .map((person, i) => {
                      if (person._rawBiography) {
                        return (
                          <Clamp 
                            key={person.id}
                            lines="5"
                            css={css`
                              margin-bottom: 2rem;
                            `}
                          >
                            <PortableText
                              blocks={person._rawBiography}
                              setEmbed={setEmbed}
                            />
                          </Clamp>
                        )
                      } else {
                        return (
                          <div 
                            key={person.id}
                            css={css`
                              font-weight: bold;
                              margin-bottom: 2rem;
                            `}
                          >
                            {person.name}
                          </div>
                        )
                      }
                    })
                  }
                </Accordion>
              }
              {data.program?.information.length > 0 && data.program.information
                .map(information =>
                  <Accordion
                    key={information._key}
                    title={information.title}
                  >
                    <PortableText
                      blocks={information._rawContent}
                      setEmbed={setEmbed}
                    />
                  </Accordion>
                )
              }
              {people.accessibilityStatement &&
                <Accordion title="Accessibility">
                  <PortableText
                    blocks={data.program.accessibilityStatement && data.program.accessibilityStatement._rawContent}
                    setEmbed={setEmbed}
                  />
                </Accordion>
              }
            </div>
          </div>
        </div>
      </div>
      {data.program.related.length > 0 &&
        <div
          css={css`
            padding-top: 0.7rem;
            border-top: 1px solid var(--black);
          `}
        >
          <h3
            className="type--medium"
            css={css`
              margin-bottom: 6rem;
            `}
          >Related</h3>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(12, 1fr);
              gap: var(--gutter);
            `}
          >
            {data.program.related.map((program, i) =>
              <ProgramCard
                key={`${program.id}-${i}`}
                css={css`
                  grid-column: span 6;

                  @media (max-width: 700px) {
                    grid-column: span 12;
                  }
                `}
                program={program}
              />
            )}
          </div>
        </div>
      }
      <Gallery
        current={currentImage}
        css={css`
          display: ${(showImages) ? `block` : `none`};
          line-height: 0;
        `}
        setShowImages={setShowImages}
      >
        {data.program.featureImage && <div
          data-caption={data.program.featureImage.asset?.description}
        >
          <GatsbyImage
            image={data.program.featureImage.asset?.gatsbyImageData}
            alt={data.program.featureImage.asset?.altText}
            objectFit="contain"
            loading="eager"
            css={css`
              max-height: 100vh;
              margin: 0 auto;
            `}
          />
        </div>}
        {data.program.images
          .map((image, i) => (
            <div
              key={i}
              data-caption={image.asset?.description}
            >
              <GatsbyImage
                image={image.asset?.gatsbyImageData}
                alt={image.asset?.altText}
                objectFit="contain"
                loading="eager"
                css={css`
                  max-height: 100vh;
                  margin: 0 auto;
                `}
              />
            </div>
          ))
        }
      </Gallery>
    </div>
  );
}

export const query = graphql`
  query ProgramTemplateQuery($slug: String!) {
    program: sanityProgram(slug: {current: {eq: $slug}}) {
      featureImage {
        ...Image
        asset {
          altText
          description
          gatsbyImageData(width: 1980, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      featureVideo
      title
      startDate
      endDate
      people {
        id
        name
        _rawBiography(resolveReferences: {maxDepth: 10})
      }
      curators {
        id
        name
        _rawBiography(resolveReferences: {maxDepth: 10})
      }
      _rawContent(resolveReferences: {maxDepth: 10})
      images {
        asset {
          altText
          description
          gatsbyImageData(width: 1980, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      _rawOverview(resolveReferences: {maxDepth: 10})
      _rawKeyDatesTimes(resolveReferences: {maxDepth: 10})
      acknowledgement {
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      information {
        _key
        title
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      accessibilityStatement {
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      publications {
        slug {
          current
        }
        title
        author
      }
      location
      type {
        title
      }
      series {
        title
      }
      related {
        id
        slug {
          current
        }
        featureImage {
          ...Image
          asset {
            altText
            description
            gatsbyImageData(sizes: "50vw", aspectRatio: 1.6, placeholder: NONE, layout: FULL_WIDTH)
          }
        }
        startDate
        endDate
        title
        people {
          id
          name
        }
        location
        type {
          title
        }
        series {
          title
        }
      }
    }
  }
`
